import { useState, useEffect, useCallback } from 'react';
import { championshipApi } from '../lib/api';
// FASE 2.2/2.3 — aviso explícito do que o Lançamento Direto SUBSTITUI nesta
// chave (resultado já lançado no Placar/Mesa e/ou medalhas da premiação).
import { buildDirectPlacementWarning, buildUndoDirectPlacementWarning } from '../lib/warnings';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Loader2, Save, AlertTriangle, CheckCircle, ArrowRight, RotateCcw } from 'lucide-react';
import { toast } from 'sonner';
import SearchableCombobox from './SearchableCombobox';

const PLACE_LABELS = ['1º Lugar', '2º Lugar', '3º Lugar', '4º Lugar'];
const PLACE_EMOJIS = ['🥇', '🥈', '🥉', '🏅'];

const DELIVERY_OPTIONS = [
  { value: 'pending', label: 'Premiação' },
  { value: 'delivered_to_athlete', label: 'Sim, entregue ao atleta' },
  { value: 'delivered_to_professor', label: 'Sim, entregue ao professor' },
  { value: 'delivered_to_guardian', label: 'Sim, entregue ao responsável' },
  { value: 'delivered_to_teammate', label: 'Sim, entregue a outro atleta da equipe' },
  { value: 'no_show', label: 'Não, não compareceu' },
  { value: 'out_of_stock', label: 'Não, acabou a medalha' },
  { value: 'arranged_later', label: 'Não, combinado entregar depois' },
];

export default function DirectPlacementSection({ championshipId, brackets, bracketNumberById }) {
  const [selectedBracket, setSelectedBracket] = useState('');
  const [bracketComboOpen, setBracketComboOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  // FASE 5.2 — desfazer o Lançamento Direto (devolve a chave às lutas)
  const [undoing, setUndoing] = useState(false);

  // Bracket athletes
  const [athletes, setAthletes] = useState([]);
  const [athleteMap, setAthleteMap] = useState({});

  // Placements state: { 1: { athlete_id, delivery_status }, 2: ... }
  const [placements, setPlacements] = useState({});
  const [justification, setJustification] = useState('');

  // Existing placement data
  const [existingPlacement, setExistingPlacement] = useState(null);
  const [hasSecondBronze, setHasSecondBronze] = useState(false);

  const currentBracket = brackets.find(b => b._id === selectedBracket);

  // Fetch bracket athletes when bracket changes
  const fetchBracketAthletes = useCallback(async () => {
    if (!selectedBracket || !championshipId) return;
    setLoading(true);
    try {
      // Get registrations for this bracket
      const { data: regs } = await championshipApi.getBrackets(championshipId, { bracket_id: selectedBracket });
      // The brackets endpoint returns matches, but we need the athletes from registrations
      // Use the bracket's registrations directly
      const bracket = brackets.find(b => b._id === selectedBracket);
      if (bracket && bracket.athletes) {
        const athMap = {};
        const athList = bracket.athletes.map(a => {
          const id = a._id || a.athlete_id;
          const name = a.name || a.athlete_name || 'Atleta';
          const team = a.team_name || '';
          athMap[id] = { name, team };
          return { value: id, label: name, sublabel: team };
        });
        setAthletes(athList);
        setAthleteMap(athMap);
      } else {
        // Fallback: extract from matches
        const athMap = {};
        const seen = new Set();
        const athList = [];
        (bracket?.matches || []).forEach(m => {
          if (m.athlete1_id && !seen.has(m.athlete1_id)) {
            seen.add(m.athlete1_id);
            athMap[m.athlete1_id] = { name: m.athlete1_name || 'Atleta', team: m.athlete1_team_name || '' };
            athList.push({ value: m.athlete1_id, label: m.athlete1_name || 'Atleta', sublabel: m.athlete1_team_name || '' });
          }
          if (m.athlete2_id && !seen.has(m.athlete2_id)) {
            seen.add(m.athlete2_id);
            athMap[m.athlete2_id] = { name: m.athlete2_name || 'Atleta', team: m.athlete2_team_name || '' };
            athList.push({ value: m.athlete2_id, label: m.athlete2_name || 'Atleta', sublabel: m.athlete2_team_name || '' });
          }
        });
        setAthletes(athList);
        setAthleteMap(athMap);
      }

      // Check for existing direct placement
      try {
        const { data } = await championshipApi.getDirectPlacement(selectedBracket);
        if (data && data.placements && Object.keys(data.placements).length > 0) {
          setExistingPlacement(data);
          setPlacements(data.placements);
          setJustification(data.justification || '');
        } else {
          setExistingPlacement(null);
          setPlacements({});
          setJustification('');
        }
        setHasSecondBronze(data?.has_second_bronze || false);
      } catch {
        setExistingPlacement(null);
        setHasSecondBronze(false);
        setPlacements({});
        setJustification('');
      }
    } catch (err) {
      toast.error('Erro ao carregar atletas da chave');
    } finally {
      setLoading(false);
    }
  }, [selectedBracket, championshipId, brackets]);

  useEffect(() => {
    fetchBracketAthletes();
  }, [fetchBracketAthletes]);

  const numAthletes = currentBracket?.num_athletes || 0;
  const maxMedalPositions = hasSecondBronze ? 4 : 3;
  const visiblePositions = Math.min(numAthletes, maxMedalPositions);

  // Get athlete options excluding already-selected ones
  const getAthleteOptions = (currentPos) => {
    const selectedIds = Object.entries(placements)
      .filter(([k]) => k !== String(currentPos))
      .map(([, v]) => v?.athlete_id)
      .filter(Boolean);
    return athletes.filter(a => !selectedIds.includes(a.value));
  };

  const updatePlacement = (pos, field, value) => {
    setPlacements(prev => ({
      ...prev,
      [pos]: {
        ...prev[pos],
        [field]: value,
        delivery_status: field === 'athlete_id'
          ? (prev[pos]?.delivery_status || 'pending')
          : (field === 'delivery_status' ? value : prev[pos]?.delivery_status),
      },
    }));
  };

  // FASE 5 — PODIO formatado + quem NÃO entra no pódio (esses continuam no
  // ranking da chave, sem colocação e com 0 ponto — o aviso lista os dois).
  const podioFormatado = () =>
    Object.entries(placements)
      .filter(([, p]) => p?.athlete_id)
      .sort((a, b) => Number(a[0]) - Number(b[0]))
      .map(([pos, p]) => `${pos}º ${athleteMap[p.athlete_id]?.name || 'Atleta'}`)
      .join(' · ');

  const semColocacaoNomes = () => {
    const noPodio = Object.values(placements)
      .filter((p) => p?.athlete_id)
      .map((p) => p.athlete_id);
    return athletes
      .filter((a) => !noPodio.includes(a.value))
      .map((a) => a.label);
  };

  // Estado VIVO da chave (somente leitura): resultado já lançado, Lançamento
  // Direto anterior e medalhas. Usado pelos avisos de salvar e de desfazer.
  const lerEstadoDaChave = async () => {
    const estado = {
      jaTemLancamentoDireto: false,
      campeaoName: '',
      medalhasCount: 0,
      temResultadoDeLuta: false,
      checagemFalhou: false,
    };
    try {
      const [rankingRes, medalsRes] = await Promise.all([
        championshipApi.getLiveRanking(championshipId),
        championshipApi.getMedals(championshipId),
      ]);
      const linhas = (rankingRes?.data || []).filter(
        (r) => r.bracket_id === selectedBracket,
      );
      estado.jaTemLancamentoDireto = linhas.some(
        (r) => r.placement_type === 'direct',
      );
      estado.campeaoName =
        linhas.find((r) => r.place === 1)?.athlete_name || '';
      estado.medalhasCount = (medalsRes?.data?.medals || []).filter(
        (m) => m.bracket_id === selectedBracket,
      ).length;
    } catch (e) {
      estado.checagemFalhou = true;
    }
    // Resultado de luta é lido das LUTAS (a colocação direta as substitui nas
    // linhas do ranking, então elas não servem de prova).
    estado.temResultadoDeLuta = (currentBracket?.matches || []).some(
      (m) => m.winner_id && m.match_type !== 'bye',
    );
    return estado;
  };

  // FASE 2.2 / 2.3 + 5.3 — antes de gravar, o operador é avisado POR EXTENSO do
  // que já existe nesta chave (resultado do Placar/Mesa, Lançamento Direto
  // anterior e/ou medalhas) e de quem continua no ranking sem colocação.
  const buildSaveWarning = async () => {
    const estado = await lerEstadoDaChave();
    return buildDirectPlacementWarning({
      ...estado,
      podio: podioFormatado(),
      semColocacao: semColocacaoNomes(),
    });
  };

  // FASE 5.2 — desfazer: apaga o lançamento (medalhas, entregas e colocações) e
  // DEVOLVE a chave ao resultado das lutas.
  const handleUndo = async () => {
    const estado = await lerEstadoDaChave();
    const msg = buildUndoDirectPlacementWarning({
      podio: podioFormatado(),
      medalhasCount: estado.medalhasCount,
      temResultadoDeLuta: estado.temResultadoDeLuta,
    });
    if (!window.confirm(msg)) return;
    setUndoing(true);
    try {
      await championshipApi.undoDirectPlacement(selectedBracket);
      toast.success('Lançamento Direto desfeito — a chave voltou ao resultado das lutas');
      setExistingPlacement(null);
      setPlacements({});
      setJustification('');
      fetchBracketAthletes();
    } catch (err) {
      toast.error(
        err?.response?.data?.detail || 'Erro ao desfazer o Lançamento Direto',
      );
    } finally {
      setUndoing(false);
    }
  };

  const handleSave = async () => {
    // Validate all positions filled
    const filled = Object.values(placements).filter(p => p?.athlete_id);
    if (filled.length !== visiblePositions) {
      toast.error(`Preencha todas as ${visiblePositions} colocações do pódio`);
      return;
    }

    if (justification.trim().length < 10) {
      toast.error('Justificativa deve ter no mínimo 10 caracteres');
      return;
    }

    // FASE 2.2/2.3 + 5.3 — confirmação explícita (o que será substituído e
    // quem continua no ranking sem colocação)
    if (!window.confirm(await buildSaveWarning())) return;

    setSaving(true);
    try {
      await championshipApi.saveDirectPlacement(selectedBracket, {
        placements,
        justification: justification.trim(),
      });
      toast.success('Resultado direto registrado com sucesso!');
      // Refresh data
      fetchBracketAthletes();
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Erro ao salvar resultado direto';
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4">
      {/* Bracket selector */}
      <div className="flex flex-col sm:flex-row gap-3 items-end">
        <div className="w-full sm:w-80">
          <Label className="text-[#A1A1AA] text-sm mb-1 block">Selecione a Chave</Label>
          <SearchableCombobox
            value={selectedBracket}
            onChange={(v) => setSelectedBracket(v)}
            options={brackets.map(b => ({
              value: b._id,
              label: `Nº ${bracketNumberById[b._id] || '?'} — ${b.category_name}`,
              sublabel: `${b.num_athletes} atletas`,
            }))}
            placeholder="Selecione uma chave..."
            searchPlaceholder="Buscar chave..."
            emptyText="Nenhuma chave encontrada"
            testId="direct-placement-bracket-select"
          />
        </div>
      </div>

      {!selectedBracket ? (
        <Card className="bg-[#141414] border-[#27272A]">
          <CardContent className="flex flex-col items-center justify-center py-12">
            <ArrowRight className="w-12 h-12 text-[#71717A] mb-3" />
            <h3 className="text-lg font-bold text-white mb-1">Selecione uma chave</h3>
            <p className="text-[#71717A] text-sm text-center">
              Escolha a chave para registrar o resultado direto por colocação
            </p>
          </CardContent>
        </Card>
      ) : loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-6 h-6 text-[#D4AF37] animate-spin" aria-hidden="true" /><span role="status" aria-live="polite" className="ml-2 text-sm text-[#A1A1AA]">Carregando...</span>
        </div>
      ) : (
        <>
          {/* Existing placement indicator */}
          {existingPlacement && (
            <div className="p-3 rounded-lg bg-green-500/10 border border-green-500/30 flex items-center gap-2">
              <CheckCircle className="w-4 h-4 text-green-500 shrink-0" />
              <span className="text-sm text-green-400">
                Esta chave já possui resultado direto registrado
                {existingPlacement.performed_at && (
                  <span className="text-[#71717A] ml-2">
                    ({new Date(existingPlacement.performed_at).toLocaleString('pt-BR')})
                  </span>
                )}
              </span>
            </div>
          )}

          {/* Category header */}
          <div className="p-3 rounded-lg bg-[#1A1A1A] border border-[#27272A]">
            <div className="text-[#71717A] text-xs uppercase tracking-wide">Chave</div>
            <div className="text-white font-medium text-sm">
              Nº {bracketNumberById[selectedBracket] || '?'} — {currentBracket?.category_name}
            </div>
            <div className="text-[#A1A1AA] text-xs mt-0.5">
              {numAthletes} atletas · {visiblePositions} colocações
            </div>
          </div>

          {/* Placements — HORIZONTAL layout */}
          <div className="overflow-x-auto">
            <div className="flex gap-3 min-w-0 pb-2" style={{ minWidth: `${visiblePositions * 220}px` }}>
              {Array.from({ length: visiblePositions }, (_, i) => i + 1).map(pos => {
                const posStr = String(pos);
                const emoji = PLACE_EMOJIS[pos - 1] || '🎖️';
                const label = PLACE_LABELS[pos - 1] || `${pos}º Lugar`;
                const current = placements[posStr] || {};
                const options = getAthleteOptions(pos);

                return (
                  <div
                    key={pos}
                    className="flex-1 min-w-[200px] max-w-[260px] p-3 rounded-lg border border-[#27272A] bg-[#141414]"
                  >
                    <div className="text-center mb-3">
                      <span className="text-xl">{emoji}</span>
                      <span className="text-white font-bold text-sm ml-1">{label}</span>
                    </div>

                    {/* Athlete selector */}
                    <div className="mb-2">
                      <SearchableCombobox
                        value={current.athlete_id || null}
                        onChange={(v) => updatePlacement(pos, 'athlete_id', v)}
                        options={options}
                        placeholder="Atleta"
                        searchPlaceholder="Buscar atleta..."
                        emptyText="Nenhum atleta disponível"
                        testId={`direct-placement-athlete-${pos}`}
                      />
                    </div>

                    {/* Delivery status — only for medal positions */}
                    {pos <= maxMedalPositions && (
                    <div>
                      <Select
                        value={current.delivery_status || 'pending'}
                        onValueChange={(v) => updatePlacement(pos, 'delivery_status', v)}
                      >
                        <SelectTrigger
                          className="bg-[#1A1A1A] border-[#27272A] text-white text-xs h-8"
                          data-testid={`direct-placement-delivery-${pos}`}
                        >
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-[#1A1A1A] border-[#27272A]">
                          {DELIVERY_OPTIONS.map(opt => (
                            <SelectItem key={opt.value} value={opt.value} className="text-white text-xs hover:bg-[#27272A]">
                              {opt.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Justification */}
          <div className="space-y-2">
            <Label className="text-[#A1A1AA] text-sm">
              Justificativa <span className="text-red-400">*</span>
            </Label>
            <Textarea
              value={justification}
              onChange={(e) => setJustification(e.target.value)}
              placeholder="Descreva o motivo do lançamento direto (mínimo 10 caracteres)..."
              className="bg-[#141414] border-[#27272A] text-white min-h-[80px]"
              data-testid="direct-placement-justification"
            />
            {justification.length > 0 && justification.length < 10 && (
              <p className="text-xs text-amber-500 flex items-center gap-1">
                <AlertTriangle className="w-3 h-3" />
                Mínimo de 10 caracteres ({justification.length}/10)
              </p>
            )}
          </div>

          {/* Save button */}
          <div className="flex flex-wrap items-center justify-end gap-2 pt-2">
            {/* FASE 5.2 — caminho de volta: só aparece quando a chave TEM
                Lançamento Direto (não há o que desfazer sem ele). */}
            {existingPlacement && (
              <Button
                type="button"
                variant="outline"
                onClick={handleUndo}
                disabled={undoing || saving}
                data-testid="direct-placement-undo"
                className="border-red-500/40 text-red-400 hover:bg-red-500/10"
              >
                {undoing
                  ? <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  : <RotateCcw className="w-4 h-4 mr-2" />}
                Desfazer lançamento direto
              </Button>
            )}
            <Button
              onClick={handleSave}
              disabled={saving || undoing}
              className="bg-[#D4AF37] text-black hover:bg-[#E5C158] font-semibold"
              data-testid="direct-placement-save"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
              Salvar Resultado
            </Button>
          </div>
        </>
      )}
    </div>
  );
}
